import {
    CloverHostedFieldsBrowserTokenizationSessionSchema,
    loadCloverHostedFieldsSdk,
    initializeCloverHostedFieldsDocument,
    mountCloverHostedFields,
    stringifyLosslessJson,
    type CloverHostedFieldsController,
    type CloverHostedFieldsDocument,
} from '@pillar-portal/api-types';
import { useId, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import type { PortalHostedCardEntryProps } from '@/lib/ipg/card-entry';

// Traceability: implements IPS-EINV-001
export function CloverHostedInvoiceCardEntry(
    portalHostedCardEntryProps: PortalHostedCardEntryProps
) {
    return (
        <CloverHostedInvoiceCardEntrySession
            key={stringifyLosslessJson(portalHostedCardEntryProps.delegateToken)}
            {...portalHostedCardEntryProps}
        />
    );
}

function CloverHostedInvoiceCardEntrySession({
    delegateToken,
    disabled,
    onTokenized,
    onError,
}: PortalHostedCardEntryProps) {
    const session = CloverHostedFieldsBrowserTokenizationSessionSchema.parse(delegateToken);
    const id = useId().replaceAll(':', '');
    const cardNumberId = `clover-invoice-card-number-${id}`;
    const cardDateId = `clover-invoice-card-date-${id}`;
    const cardCvvId = `clover-invoice-card-cvv-${id}`;
    const cardPostalCodeId = `clover-invoice-card-postal-code-${id}`;
    const controllerRef = useRef<CloverHostedFieldsController | null>(null);
    const onErrorRef = useRef(onError);
    const [cardholderName, setCardholderName] = useState('');
    const [ready, setReady] = useState(false);
    onErrorRef.current = onError;
    const onTokenizedRef = useRef(onTokenized);
    onTokenizedRef.current = onTokenized;
    const htmlIFrameElementRef = useRef<HTMLIFrameElement | null>(null);
    const [hostedDocument, setHostedDocument] = useState<Document | null>(null);

    useLayoutEffect(() => {
        const htmlIFrameElement = htmlIFrameElementRef.current;
        if (htmlIFrameElement === null) return;
        const abortController = new AbortController();
        let cloverHostedFieldsDocument: CloverHostedFieldsDocument | undefined;
        initializeCloverHostedFieldsDocument({
            htmlIFrameElement,
            sdkUrl: session.sdk_url,
            abortSignal: abortController.signal,
        })
            .then((initializedCloverHostedFieldsDocument) => {
                if (abortController.signal.aborted) {
                    initializedCloverHostedFieldsDocument.dispose();
                    return;
                }
                cloverHostedFieldsDocument = initializedCloverHostedFieldsDocument;
                setHostedDocument(initializedCloverHostedFieldsDocument.document);
            })
            .catch((error: unknown) => {
                if (!abortController.signal.aborted) {
                    onErrorRef.current(
                        error instanceof Error
                            ? error.message
                            : 'Unable to initialize Clover secure card entry.'
                    );
                }
            });
        return () => {
            abortController.abort();
            controllerRef.current = null;

            cloverHostedFieldsDocument?.dispose();
        };
    }, [session.api_access_key, session.merchant_id, session.sdk_url]);

    useLayoutEffect(() => {
        if (hostedDocument === null) return;
        let active = true;
        const initialize = async (): Promise<void> => {
            await loadCloverHostedFieldsSdk(hostedDocument, session.sdk_url);
            if (!active) return;
            controllerRef.current = mountCloverHostedFields({
                document: hostedDocument,
                session,
                targets: {
                    cardNumber: `#${cardNumberId}`,
                    cardDate: `#${cardDateId}`,
                    cardCvv: `#${cardCvvId}`,
                    cardPostalCode: `#${cardPostalCodeId}`,
                },
            });
            setReady(true);
        };
        initialize().catch((error: unknown) => {
            if (active) {
                onErrorRef.current(
                    error instanceof Error
                        ? error.message
                        : 'Unable to load Clover secure card entry.'
                );
            }
        });
        return (): void => {
            active = false;
        };
    }, [
        hostedDocument,
        cardCvvId,
        cardDateId,
        cardNumberId,
        cardPostalCodeId,
        session.api_access_key,
        session.merchant_id,
        session.sdk_url,
    ]);

    const tokenize = async (event: FormEvent): Promise<void> => {
        event.preventDefault();
        const normalizedName = cardholderName.trim();
        if (normalizedName.length === 0) {
            onError('Name on card is required.');
            return;
        }
        const controller = controllerRef.current;
        try {
            if (controller === null) {
                throw new Error('Clover secure card entry is not ready.');
            }
            const tokenizationResult = await controller.createToken();
            if (controllerRef.current !== controller) return;
            onTokenizedRef.current({
                token: tokenizationResult.token,
                tokenizationResult,
                cardholderName: normalizedName,
            });
        } catch (error: unknown) {
            if (controllerRef.current === controller) {
                onErrorRef.current(
                    error instanceof Error ? error.message : 'Unable to secure card details.'
                );
            }
        }
    };

    return (
        <form className="space-y-3" onSubmit={(event) => void tokenize(event)}>
            <label
                className="block text-sm font-medium"
                htmlFor={`clover-invoice-cardholder-name-${id}`}
            >
                Name on card
            </label>
            <input
                id={`clover-invoice-cardholder-name-${id}`}
                autoComplete="cc-name"
                disabled={disabled}
                value={cardholderName}
                onChange={(event) => setCardholderName(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <iframe
                ref={htmlIFrameElementRef}
                title="Clover secure invoice card entry"
                data-clover-owned-document=""
                inert={disabled}
                style={{ width: '100%', height: 0, border: 0, display: 'block' }}
            >
                {hostedDocument === null
                    ? null
                    : createPortal(
                          <div className="space-y-3">
                              <label className="block text-sm font-medium" htmlFor={cardNumberId}>
                                  Card number
                              </label>
                              <div id={cardNumberId} className="h-16 rounded-md border p-3" />
                              <label className="block text-sm font-medium" htmlFor={cardDateId}>
                                  Expiration date
                              </label>
                              <div id={cardDateId} className="h-16 rounded-md border p-3" />
                              <label className="block text-sm font-medium" htmlFor={cardCvvId}>
                                  CVV
                              </label>
                              <div id={cardCvvId} className="h-16 rounded-md border p-3" />
                              <label
                                  className="block text-sm font-medium"
                                  htmlFor={cardPostalCodeId}
                              >
                                  Card billing ZIP
                              </label>
                              <div id={cardPostalCodeId} className="h-16 rounded-md border p-3" />
                          </div>,
                          hostedDocument.body
                      )}
            </iframe>
            <button
                type="submit"
                disabled={disabled || !ready}
                className="h-10 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
                Secure card details
            </button>
        </form>
    );
}
